import React from "react";
// ConsumerDetail left card

// Default
import { useState } from "react";

// Mui
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid2";
import useMediaQuery from "@mui/material/useMediaQuery";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";

// Custom
import CustomSearch from "../search/Search";
import DateAndTimePopover from "../popovers/DateAndTimePopover";
import MapViewPopover from "../popovers/MapViewPopover";

// Svg
import Location from "../../assets/svg/location";
import Calendar from "../../assets/svg/calendar";
import Clock from "../../assets/svg/clock";

// Theme
import theme from "../../theme";

// Redux
import { setCustomerId } from "../../redux/features/CustomerSlice";

import { useAppDispatch, useAppSelector } from "../../redux/hooks";

const ConsumerDetailLeftCard = ({
  handleSearch,
}: {
  handleSearch: () => void;
}) => {
  const matches = useMediaQuery(theme.breakpoints.down("lg"));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [timeRange, setTimeRange] = useState<number[]>([0, 1439]);
  const [mapView, setMapView] = useState<HTMLElement | null>(null);
  const dispatch = useAppDispatch();
  const { customerDetails, customerId } = useAppSelector(
    (state) => state.customer,
  );

  const handleDateChange = (dates: [Date, Date]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  return (
    <Card className="consumer_left_card">
      <Grid container pb={"12.5px"}>
        <Grid size={{ xs: 4, md: 4, lg: 4 }} container alignItems={"center"}>
          <Stack direction={"row"} alignItems={"center"} gap={"2px"}>
            <Typography variant="body1">Consumer ID: </Typography>
            <CustomSearch
              value={customerId}
              onChange={(e) => dispatch(setCustomerId(e.target.value))}
              onClick={handleSearch}
              sx={{ width: matches ? "160px" : "250px" }}
            />
          </Stack>
        </Grid>

        <Grid
          size={{ xs: 4, md: 3, lg: 4 }}
          container
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Stack>
            <Typography
              variant="body1"
              color="#1280E7"
              fontSize={matches ? "12px" : undefined}
            >
              {customerDetails?.name}
            </Typography>
          </Stack>
        </Grid>

        <Grid
          size={{ xs: 4, md: 5, lg: 4 }}
          container
          justifyContent={"flex-end"}
          alignItems={"center"}
        >
          <Stack>
            <Typography variant="body1">
              Registered Contact Number :{" "}
              <Typography
                color="#1280E7"
                fontWeight={600}
                letterSpacing={"0.32px"}
              >
                {" "}
                {customerDetails?.registerContactNumber}
              </Typography>
            </Typography>
          </Stack>
        </Grid>
      </Grid>

      <Divider
        sx={{
          borderColor: "#CACDD7",
        }}
      />

      <Grid container mt={"18px"} mb={"28px"}>
        <Grid
          size={{ xs: 4, md: 4.3, lg: 4 }}
          container
          pl={matches ? "5px" : "24px"}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={"8px"}
            textAlign={"center"}
          >
            <Typography variant="caption" textAlign={"right"}>
              Location:
            </Typography>
            <Location />
            <Typography variant="caption">
              {customerDetails?.location}
              <Typography
                color="#017EF4"
                fontSize={"11px"}
                onClick={(e) => setMapView(e.currentTarget as HTMLElement)}
              >
                {" "}
                &nbsp;View
              </Typography>
            </Typography>
          </Stack>
        </Grid>

        <Grid
          size={{ xs: 4, md: 2.7, lg: 4 }}
          container
          justifyContent={"center"}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={"8px"}
            textAlign={"center"}
          >
            <Typography variant="caption" textAlign={"right"}>
              Account Status:
            </Typography>
            <Chip
              label={customerDetails?.accountStatus}
              className="consumer_left_card_status_chip"
            />
          </Stack>
        </Grid>

        <Grid
          size={{ xs: 4, md: 5, lg: 4 }}
          container
          justifyContent={"flex-end"}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={"8px"}
            textAlign={"center"}
          >
            <Typography variant="caption" textAlign={"right"}>
              Last Comm. Time:
            </Typography>
            <Clock />
            <Typography variant="caption">
              {customerDetails?.lastCommonTime}
            </Typography>
          </Stack>
        </Grid>
      </Grid>

      <Grid container>
        <Grid size={{ xs: 4, md: 3, lg: 4 }} container>
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={"8px"}
            textAlign={"center"}
          >
            <Typography variant="caption" textAlign={"right"}>
              Billing Cycle:
            </Typography>
            <IconButton
              onClick={(event) =>
                setAnchorEl(event.currentTarget as HTMLElement)
              }
            >
              <Calendar />
            </IconButton>
            <Typography variant="caption">
              {customerDetails?.billingCycle}
            </Typography>
          </Stack>
        </Grid>

        <Grid
          size={{ xs: 4, md: 5, lg: 4 }}
          container
          justifyContent={"center"}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={"8px"}
            textAlign={"center"}
          >
            <Typography variant="caption" textAlign={"right"}>
              Last Payment Date:
            </Typography>
            <IconButton
              onClick={(event) =>
                setAnchorEl(event.currentTarget as HTMLElement)
              }
            >
              <Calendar />
            </IconButton>
            <Typography variant="caption">
              {customerDetails?.lastPaymentDate}
            </Typography>
          </Stack>
        </Grid>

        <Grid
          size={{ xs: 4, md: 4, lg: 4 }}
          container
          justifyContent={"flex-end"}
        >
          <Box
            sx={{
              border: "4px solid #9CE976",
              borderRadius: "100%",
              marginRight: "5px",
            }}
          >
            <Box sx={{ width: "10px", Height: "10px" }}></Box>
          </Box>
          <Stack direction={"row"} alignItems={"center"} gap={"8px"}>
            <Typography variant="caption" textAlign={"right"}>
              Outstanding Balance:
            </Typography>
            <Stack direction={"row"} gap={"10px"} alignItems={"center"}>
              <Typography variant="caption">
                &#8377; {customerDetails?.outStandingBalance}{" "}
              </Typography>
            </Stack>
          </Stack>
        </Grid>
      </Grid>

      <Grid container>
        <Grid size={{ xs: 4, md: 3, lg: 4 }} container>
          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={"8px"}
            textAlign={"center"}
          >
            <Typography variant="caption" textAlign={"right"}>
              Billing Type:
            </Typography>
            <Typography variant="caption">
              {customerDetails?.additionalInfo?.consumerBillingType}
            </Typography>
          </Stack>
        </Grid>
      </Grid>

      <DateAndTimePopover
        anchorEl={anchorEl}
        setAnchorEl={(anchorEl) => setAnchorEl(anchorEl as HTMLElement | null)}
        startDate={startDate}
        endDate={endDate}
        timeRange={timeRange}
        setTimeRange={(timeRange) => setTimeRange(timeRange as number[])}
        handleDateChange={handleDateChange}
        handleDateSubmit={() => {}}
      />

      <MapViewPopover
        mapView={mapView}
        setMapView={(mapView) => setMapView(mapView as HTMLElement | null)}
      />
    </Card>
  );
};

export default ConsumerDetailLeftCard;
